import React from "react";
import {Box, DialogContent, Dialog} from "@mui/material";
import BottomBarUI from "./components/bottombar/BottomBarUI";
import ProblemContentUI from "./components/content/ProblemContentUI";
import FullScreenLoaderSpinner from "../../components/full-screen-loader-spinner";
import AIAssistantFeature, {AIAssistantRefHandle} from "../ai-assistant";
import {Panel, PanelGroup, PanelResizeHandle} from "react-resizable-panels";
import CloseIconUI from "./components/topbar/CloseIconUI";
import {PanelState, usePanelControl} from "../../hooks/usePanelControl";
import {ProblemType} from "../../config/enums";
import {STYLES} from "./STYLES";
import {useParams} from "react-router-dom";
import useDialogControl from "./hooks/useDialogControl";
import useQuestionControl from "./hooks/useQuestionControl";
import {useGetQuestionListQuery} from "../../store/api/problemsetApiSlice";
import {
  useGetExamConfigQuery,
  useUpdateProblemsetFiltersMutation,
} from "../../store/api/examConfigApiSlice";
import {useExamPageNavigate} from "../../pages/exam/common/navigation/useExamPageNavigate";
import {ExamPageSection} from "../../pages/exam/common/navigation/routes";

export default function ProblemDialog() {
  const {questionId} = useParams();

  /** DIALOG CONTROL */
  const {isDialogOpen, handleDialogClose, handleDialogCleanUp} =
    useDialogControl();

  /** PANEL CONTROL */
  const {panelRef, panelState, resizePanel} = usePanelControl(
    panelProps.initialState,
    panelProps.minSize,
    panelProps.maxSize,
  );
  const openPanel = () => {
    resizePanel(PanelState.COLLAPSED);
  };

  /** AI ASSISTANT */
  const aiAssitantRef = React.useRef<AIAssistantRefHandle>(null);

  /** QUESTION API */
  const {
    questionData,
    isQuestionFetching,
    isQuestionSubmitted,
    handleSubmitQuestion,
    handleQuestionResponseonUpdate,
  } = useQuestionControl(questionId);

  const onSubmitQuestion = () => {
    const isSuccess = handleSubmitQuestion();
    if (!isSuccess) return;

    aiAssitantRef.current?.handleSendMessage(
      "Give me the solution for this question.",
    );
  };

  /** QUESTION LIST API */
  const {data: examConfigData} = useGetExamConfigQuery();
  const [updateProblemsetFilters] = useUpdateProblemsetFiltersMutation();
  const selectedSubject = examConfigData?.problemsetFilters.subjects?.find(
    subject => subject.isSelected,
  );
  const selectedChapter = selectedSubject?.chapters?.find(
    chapter => chapter.isSelected,
  );
  const isValidSubjectAndChapter =
    !!selectedSubject?.subjectId && !!selectedChapter?.chapterId;
  const selectedPage = selectedChapter?.page ? Number(selectedChapter.page) : 1;
  const {data} = useGetQuestionListQuery(
    {
      subjects: [selectedSubject!.subjectId],
      chapters: [selectedChapter!.chapterId],
      attemptStatus: selectedChapter?.user_question_status ?? [],
      patterns: selectedChapter?.question_types ?? [],
      difficulty: selectedChapter?.difficulty_types ?? [],
      feature: selectedChapter?.feature,
      page: selectedPage,
    },
    {
      skip: !isValidSubjectAndChapter,
    },
  );

  const questionList = data?.rowsData ?? [];
  const currentQuestionIndex = questionList?.findIndex(
    question => question.id === questionId,
  );

  const examPageNavigate = useExamPageNavigate();
  const navigateToQuestion = (qId: string) => {
    examPageNavigate(
      ExamPageSection.PROBLEM_SET_QUESTION,
      {
        questionId: qId,
      },
      undefined,
      true,
    );
  };
  const navigateToProblemSet = () => {
    examPageNavigate(ExamPageSection.PROBLEM_SET, undefined);
  };

  const onNextQuestionClick = async () => {
    const nextQuestion = questionList?.[currentQuestionIndex + 1];
    if (nextQuestion) {
      navigateToQuestion(nextQuestion.id);
    } else {
      const isNextPageAvailable = !!data?.pagination?.next;
      if (isNextPageAvailable && isValidSubjectAndChapter) {
        await updateProblemsetFilters({
          subjectId: selectedSubject.subjectId,
          chapterId: selectedChapter.chapterId,
          page: selectedPage + 1,
        });
        navigateToProblemSet();
      } else {
        navigateToProblemSet();
      }
    }
  };
  const onPreviousQuestionClick = async () => {
    const previousQuestion = questionList?.[currentQuestionIndex - 1];
    if (previousQuestion) {
      navigateToQuestion(previousQuestion.id);
    } else {
      const isPreviousPageAvailable = !!data?.pagination?.previous;
      if (isPreviousPageAvailable && isValidSubjectAndChapter) {
        await updateProblemsetFilters({
          subjectId: selectedSubject.subjectId,
          chapterId: selectedChapter.chapterId,
          page: selectedPage - 1,
        });
        navigateToProblemSet();
      } else {
        navigateToProblemSet();
      }
    }
  };

  return (
    <Dialog
      open={isDialogOpen}
      onClose={handleDialogClose}
      aria-labelledby="problem-modal-title"
      TransitionProps={{
        onExited: () => {
          handleDialogCleanUp();
        },
      }}
      slotProps={STYLES.DialogSlotProps}
      PaperProps={STYLES.DialogPaperProps}>
      <CloseIconUI onClick={handleDialogClose} />
      {questionData && (
        <PanelGroup direction="horizontal">
          <Panel>
            <DialogContent sx={STYLES.dialogContent}>
              <Box sx={{flexGrow: 1}}>
                <ProblemContentUI
                  isSubmitted={isQuestionSubmitted}
                  questionData={questionData}
                  onSubmitQuestion={onSubmitQuestion}
                  onUpdateQuestionResponse={handleQuestionResponseonUpdate}
                  onOpenPanel={openPanel}
                />
              </Box>
              <Box sx={STYLES.bottomBarContainer}>
                <Box>
                  <BottomBarUI
                    isSubmited={isQuestionSubmitted}
                    onSubmitButtonClick={onSubmitQuestion}
                    onNextButtonClick={onNextQuestionClick}
                    onPreviousButtonClick={onPreviousQuestionClick}
                  />
                </Box>
              </Box>
            </DialogContent>
          </Panel>
          <PanelResizeHandle />
          <Panel
            ref={panelRef}
            {...panelProps}
            style={{
              display: panelState === PanelState.CLOSED ? "none" : undefined,
            }}>
            <AIAssistantFeature
              ref={aiAssitantRef}
              panelState={panelState}
              onResizeCallback={resizePanel}
              surface={{
                surfaceType: "QUESTION",
                surfaceId: questionData.id,
                context: questionData.status,
              }}
              isAutoFocusDisabled={questionData.qType === ProblemType.Numerical}
            />
          </Panel>
        </PanelGroup>
      )}
      {isQuestionFetching && <FullScreenLoaderSpinner />}
    </Dialog>
  );
}

const panelProps = {
  initialState: PanelState.COLLAPSED,
  minSize: 25,
  maxSize: 40,
  defaultSize: 25,
  collapsedSize: 25,
  collapsible: true,
};
